import React, { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { FilterSystem } from './components/FilterSystem';
import { FilterSchema } from './types';

export { FilterSystem } from './components/FilterSystem';
export { useFilters } from './hooks/useFilters';
export { useTranslations } from './hooks/useTranslations';

/**
 * Mount options for FilterSystem
 */
export interface MountOptions {
  /** Filter schema defining available filters */
  schema: FilterSchema;
  /** Panel title (defaults to "Filters") */
  title?: string;
  /** Disable interactions (e.g., while loading) */
  disabled?: boolean;
  /**
   * Optional render function to provide filter controls.
   * Useful when EJS templates want to inject inputs/selects.
   */
  renderControls?: () => ReactNode;
}

/**
 * Mount FilterSystem into a DOM element
 *
 * Usage in EJS:
 * ```html
 * <div id="filter-root"></div>
 * <script>
 *   window.CarrotFilters.mount('#filter-root', {
 *     schema: { page: 'groups', facets: [...] },
 *     renderControls: () => { ... }
 *   });
 * </script>
 * ```
 */
export function mount(target: string | HTMLElement, opts: MountOptions): void {
  const el = typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target;

  if (!el) {
    throw new Error(`CarrotFilters.mount: target not found for "${String(target)}"`);
  }

  const root = createRoot(el);

  // Handle renderControls output (could be string HTML or React node)
  const controlsContent = opts.renderControls ? opts.renderControls() : null;
  const controlsElement = typeof controlsContent === 'string'
    ? <div dangerouslySetInnerHTML={{ __html: controlsContent }} />
    : controlsContent;

  root.render(
    <FilterSystem
      schema={opts.schema}
      title={opts.title ?? 'Filters'}
      disabled={opts.disabled}
    >
      {controlsElement}
    </FilterSystem>
  );
}

/**
 * Global window interface extension
 */
declare global {
  interface Window {
    CarrotFilters: {
      mount: typeof mount;
      FilterSystem: typeof FilterSystem;
    };
  }
}

/**
 * Expose global API for EJS integration
 */
if (typeof window !== 'undefined') {
  window.CarrotFilters = window.CarrotFilters || {
    mount,
    FilterSystem,
  };
}
